import React, { Fragment, useEffect, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  // Table as ReactTable,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";

import { ProductsGlobalFilter } from "../../Components/Common/GlobalSearchFilter";
import { Pagination, TablePagination } from "@mui/material";

const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control search"
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

// Column Filter
const Filter = ({
  column,
  // table
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={columnFilterValue ?? ""}
        onChange={(event) => column.setFilterValue(event.target.value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  );
};

const TableContainer = ({
  columns,
  data,
  pagination,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  onGetPage,
  onSortChange,
  currentSortBy,
  currentSortAsc,
}) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const { currentPage, pageSize, totalItems, totalPages } = pagination;

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      pagination: { pageIndex: currentPage, pageSize: pageSize },
    },
    manualPagination: true,
    rowCount: totalItems,
    pageCount: totalPages,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageIndex, setPageSize } = table;

  useEffect(() => {
    pageSize && setPageSize(pageSize);
  }, [pageSize, setPageSize]);

  useEffect(() => {
    if (pagination) {
      setPageIndex(pagination.currentPage);
      setPageSize(pagination.pageSize);
    }
  }, [pagination]);

  const handleChangePage = (event, newPage) => {
    onGetPage(newPage, pageSize);
  };
  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    onGetPage(0, parseInt(event.target.value, 10));
  };
  return (
    <Fragment>
      {data && data.length > 0 ? (
        <div className={divClass}>
          <Table hover className={tableClass}>
            <thead className={theadClass}>
              {getHeaderGroups().map((headerGroup) => (
                <tr className={trClass} key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const colId = header.column.columnDef.accessorKey || header.column.id;
                    const isSortable = !!header.column.columnDef.accessorKey && onSortChange;
                    const isCurrentSort = isSortable && currentSortBy === colId;
                    return (
                      <th
                        key={header.id}
                        className={thClass}
                        style={isSortable ? { cursor: 'pointer', userSelect: 'none' } : {}}
                        onClick={() => {
                          if (isSortable) {
                            onSortChange(colId, isCurrentSort ? !currentSortAsc : true);
                          }
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <React.Fragment>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {isCurrentSort ? (currentSortAsc ? " ▲" : " ▼") : null}
                            {header.column.getCanFilter() ? (
                              <div>
                                <Filter column={header.column} table={table} />
                              </div>
                            ) : null}
                          </React.Fragment>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody>
              {getRowModel().rows.map((row) => {
                return (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <TablePagination
                sx={{ ml: "auto" }}
                rowsPerPageOptions={[
                  5,
                  10,
                  25,
                  50,
                  100,
                  { label: "All", value: -1 },
                ]}
                count={totalItems}
                page={currentPage}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                showFirstButton
                showLastButton
              />
            </tfoot>
          </Table>
        </div>
      ) : (
        <div className="py-4 text-center">
          <div>
            <lord-icon
              src="https://cdn.lordicon.com/msoeawqm.json"
              trigger="loop"
              colors="primary:#405189,secondary:#0ab39c"
              style={{ width: "72px", height: "72px" }}
            ></lord-icon>
          </div>

          <div className="mt-4">
            <h5>Sorry! No Users Found</h5>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default TableContainer;
/*
 <Pagination
          count={totalPages}
          page={currentPage}
          variant="outlined"
          color="primary"
          shape="rounded"
          onChange={handleChangePage}
          showFirstButton
          showLastButton
        />
<div className="col-sm-auto">
          <ul className="pagination pagination-wrap pagination-separated pagination-sm justify-content-center justify-content-sm-start mb-0">
            <li
              className={
                !getCanPreviousPage() ? "page-item disabled" : "page-item"
              }
            >
              <Link to="#" className="page-link" onClick={onPreviousPage}>
                Previous
              </Link>
            </li>
            <div style={{ width: "100%" }} className="d-flex ">
              {getPageOptions().map((item, key) => (
                <React.Fragment key={key}>
                  <li className="page-item">
                    <Link
                      to="#"
                      className={
                        currentPage === item ? "page-link active" : "page-link"
                      }
                      onClick={() => onGetPage(item)}
                    >
                      {item + 1}
                    </Link>
                  </li>
                </React.Fragment>
              ))}
            </div>
            <li
              className={!getCanNextPage() ? "page-item disabled" : "page-item"}
            >
              <Link to="#" className="page-link" onClick={onNextPage}>
                Next
              </Link>
            </li>
          </ul>
        </div>
        */
